/* eslint-disable no-useless-catch */
/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */
/* eslint-disable consistent-return */

import Cookies from "js-cookie";
import { API } from "../config/axios";

interface OverviewCardsResponse {
  status?: string;
  statusCode?: string;
  message?: string;
  data?: {
    totalAmountMade?: number;
    totalCompanies?: number;
  };
}

interface CompaniesView {
  id?: number;
  companyName?: string | null;
  companyStatus?: number;
  companyLogo?: string | null;
}

interface PaginatedCompaniesResponse {
  status?: string;
  statusCode?: string;
  message?: string;
  data?: {
    data?: CompaniesView[] | null;
    pageNumber?: number;
    pageSize?: number;
    totalCount?: number;
    totalPages?: number;
    hasPreviousPage?: boolean;
    hasNextPage?: boolean;
  };
}

interface CompanyChartView {
  id?: number;
  companyName?: string | null;
  totalPremium?: number;
  totalBasic?: number;
}

interface ChartResponse {
  status?: string;
  statusCode?: string;
  message?: string;
  data?: CompanyChartView[] | null;
}

class Billing {
  getOverviewCards = async (): Promise<OverviewCardsResponse> => {
    try {
      const response = await API.get<OverviewCardsResponse>(
        "Billing/OverViewCards",
        {
          headers: {
            "X-Client-Version": "2.0",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  getCompaniesList = async (
    pageNumber?: number,
    pageSize?: number,
    searchText?: string,
    statusFilter?: string
  ): Promise<PaginatedCompaniesResponse> => {
    try {
      const params = new URLSearchParams();
      if (pageNumber !== undefined) {
        params.append("pageNumber", pageNumber.toString());
      }
      if (pageSize !== undefined) {
        params.append("pageSize", pageSize.toString());
      }
      if (searchText) {
        params.append("searchText", searchText);
      }
      if (statusFilter && statusFilter !== "all") {
        const status = statusFilter === "active" ? "Active" : "Inactive";
        params.append("status", status);
      }

      const queryString = params.toString();
      const url = `Billing/CompaniesList${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await API.get<PaginatedCompaniesResponse>(url, {
        headers: {
          "X-Client-Version": "2.0",
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  getChart = async (
    month?: string,
    companyId?: number
  ): Promise<ChartResponse> => {
    try {
      const params = new URLSearchParams();
      if (month) {
        // Convert month name to number (1-12)
        const monthNames = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const monthIndex = monthNames.indexOf(month);
        if (monthIndex !== -1) {
          const monthNumber = monthIndex + 1; // Convert 0-11 to 1-12
          params.append("Month", monthNumber.toString());
        }
      }
      if (companyId !== undefined) {
        params.append("CompanyId", companyId.toString());
      }

      const queryString = params.toString();
      const url = `Billing/Chart${queryString ? `?${queryString}` : ""}`;

      const response = await API.get<ChartResponse>(url, {
        headers: {
          "X-Client-Version": "2.0",
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  getSubscriptionData = async (
    companyId?: number,
    month?: string,
    pageSize?: number,
    pageNumber?: number
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      if (companyId !== undefined) {
        params.append("CompanyId", companyId.toString());
      }
      if (month) {
        params.append("Month", month);
      }
      if (pageSize !== undefined) {
        params.append("PageSize", pageSize.toString());
      }
      if (pageNumber !== undefined) {
        params.append("PageNumber", pageNumber.toString());
      }

      const queryString = params.toString();
      const url = `Billing/SubscriptionData${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await API.get(url, {
        headers: {
          "X-Client-Version": "2.0",
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  getCompanyInvoices = async (
    companyId?: number,
    month?: string,
    year?: string,
    pageSize?: number,
    pageNumber?: number
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      if (companyId !== undefined) {
        params.append("CompanyId", companyId.toString());
      }
      if (month) {
        params.append("Month", month);
      }
      if (year) {
        params.append("Year", year);
      }
      if (pageSize !== undefined) {
        params.append("PageSize", pageSize.toString());
      }
      if (pageNumber !== undefined) {
        params.append("PageNumber", pageNumber.toString());
      }

      const queryString = params.toString();
      const url = `Billing/DisplayCompanyInvoices${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await API.get(url, {
        headers: {
          "X-Client-Version": "2.0",
        },
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  bulkExport = async (params: {
    startDate?: string;
    endDate?: string;
    companyId?: number[];
    subscriptionTypes?: string[];
  }): Promise<Response> => {
    // Build request body - only include fields that are provided
    const requestBody: any = {};
    if (params.startDate) {
      requestBody.startDate = params.startDate;
    }
    if (params.endDate) {
      requestBody.endDate = params.endDate;
    }
    if (params.companyId && params.companyId.length > 0) {
      requestBody.companyId = params.companyId;
    }
    if (params.subscriptionTypes && params.subscriptionTypes.length > 0) {
      requestBody.subscriptionTypes = params.subscriptionTypes;
    }

    // Get the base URL from the API instance
    const baseURL = API.defaults.baseURL;
    const url = `${baseURL}/Billing/BulkExport`;

    // Get token for authorization
    const token = Cookies.get("token");
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "X-API-Version": "1.0",
      "X-API-Client": "SaferCity",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Make request with fetch
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage += `, message: ${errorData.message}`;
        }
      } catch {
        // If parsing JSON fails, just use the status message
      }
      throw new Error(errorMessage);
    }

    return response;
  };
}

export default new Billing();
